import { logger } from '../../utils/logger';
import {
  IonContent,
  IonPage,
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonText,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonSpinner,
  IonBackButton,
  IonButtons,
  IonCheckbox,
  IonIcon,
} from '@ionic/react';
import { alertCircleOutline, eyeOutline, eyeOffOutline, documentTextOutline } from 'ionicons/icons';
import { useState } from 'react';
import { supabase } from '../../services/supabase';
import { rateLimitedSignup } from '../../services/rateLimitedAuthService';
import { Capacitor } from '@capacitor/core';
import './Auth.css';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isOver18, setIsOver18] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setErrorDetails('');

    // Validate age verification
    if (!isOver18) {
      setError('You must be 18 or older to use Dice Cat');
      setLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to continue');
      setLoading(false);
      return;
    }

    // Validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      setLoading(false);
      return;
    }

    if (username.length > 20) {
      setError('Username must be 20 characters or less');
      setLoading(false);
      return;
    }

    // Username validation: alphanumeric and underscores only
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError('Username can only contain letters, numbers, and underscores (no spaces)');
      setLoading(false);
      return;
    }

    // Use deep link for mobile platforms, web URL for web
    const isNative = Capacitor.isNativePlatform();

    // For web: Use VITE_APP_URL if set (production), otherwise use current origin (local dev)
    const webBaseUrl = import.meta.env.VITE_APP_URL || window.location.origin;

    const redirectUrl = isNative
      ? 'com.dicecat.app://auth/callback'
      : `${webBaseUrl}/auth-success.html`;

    // Use rate-limited signup (3 attempts per hour)
    const result = await rateLimitedSignup(email, password, username, redirectUrl);

    if (result.error) {
      // Log full error object for debugging
      logger.error('Signup error (full):', {
        message: result.error,
        blocked: result.blocked,
        blockedUntil: result.blockedUntil,
      });

      // Provide user-friendly error messages
      let errorMessage = result.error;

      // Handle common Supabase auth errors
      if (result.error.includes('User already registered')) {
        errorMessage = 'This email is already registered. Please log in or use a different email.';
      } else if (result.error.includes('Invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (result.error.includes('Password should be')) {
        errorMessage = 'Password must be at least 6 characters long.';
      } else if (result.error.includes('Email not confirmed')) {
        errorMessage = 'Please check your email and click the confirmation link before logging in.';
      } else if (result.error.includes('duplicate') || result.error.includes('unique constraint')) {
        errorMessage = 'This username or email is already taken. Please try a different one.';
      } else if (result.error.includes('Database error') || result.error.includes('database')) {
        errorMessage = 'We encountered an issue creating your account. Please try again in a moment. If this persists, contact support.';
      } else if (result.error.includes('network') || result.error.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (result.error.includes('rate limit') || result.error.includes('Too many')) {
        // Rate limit errors are already user-friendly from the Edge Function
        errorMessage = result.error;
      }

      setError(errorMessage);
      // Store technical details for troubleshooting
      setErrorDetails(`Technical details: ${result.error} (Blocked: ${result.blocked || false})`);
      setLoading(false);
    } else {
      // Log successful signup data
      logger.log('Signup successful:', {
        user: result.user?.id,
        email: result.user?.email
      });
      setSuccess(true);
      setLoading(false);
    }
  };

  if (success) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Success!</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="auth-container">
            <div className="success-message">
              <h2>🎲 Welcome to Dice Cat!</h2>
              <p>
                We've sent a confirmation email to <strong>{email}</strong>.
              </p>
              <p>
                Click the link in the email to verify your account, then come back here and log in!
              </p>
              <IonButton expand="block" routerLink="/login">
                Go to Login
              </IonButton>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/login" />
          </IonButtons>
          <IonTitle>Create Account</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="auth-container">
          <div className="dicecat-branding">
            <img
              src="/assets/dicecat-logo-composite.svg"
              alt="Dice Cat"
              className="dicecat-composite-logo"
            />
            <p className="dicecat-tagline">Start connecting with gamers today</p>
          </div>

          <div className="auth-header">
            <h2>Create Your Account</h2>
            <p>Join the adventure</p>
          </div>

          <form onSubmit={handleSignup}>
            <IonItem className="auth-input-item">
              <IonLabel position="stacked">Username</IonLabel>
              <IonInput
                type="text"
                value={username}
                onIonInput={(e) => setUsername(e.detail.value!)}
                required
                placeholder="Choose a username"
                autocomplete="username"
              />
            </IonItem>

            <IonItem className="auth-input-item">
              <IonLabel position="stacked">Email</IonLabel>
              <IonInput
                type="email"
                value={email}
                onIonInput={(e) => setEmail(e.detail.value!)}
                required
                placeholder="your@email.com"
                autocomplete="email"
              />
            </IonItem>

            <IonItem className="auth-input-item">
              <IonLabel position="stacked">Password</IonLabel>
              <IonInput
                type={showPassword ? 'text' : 'password'}
                value={password}
                onIonInput={(e) => setPassword(e.detail.value!)}
                required
                placeholder="At least 6 characters"
                autocomplete="new-password"
              />
              <IonIcon
                slot="end"
                icon={showPassword ? eyeOffOutline : eyeOutline}
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--ion-color-medium)',
                  marginTop: '20px'
                }}
              />
            </IonItem>

            <IonItem className="auth-input-item">
              <IonLabel position="stacked">Confirm Password</IonLabel>
              <IonInput
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onIonInput={(e) => setConfirmPassword(e.detail.value!)}
                required
                placeholder="Re-enter password"
                autocomplete="new-password"
              />
              <IonIcon
                slot="end"
                icon={showConfirmPassword ? eyeOffOutline : eyeOutline}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={{
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: 'var(--ion-color-medium)',
                  marginTop: '20px'
                }}
              />
            </IonItem>

            {/* Age Verification */}
            <div className="age-verification-box">
              <div className="age-verification-header">
                <IonIcon icon={alertCircleOutline} color="warning" style={{ fontSize: '24px' }} />
                <h3>Age Requirement</h3>
              </div>
              <IonText className="age-verification-text">
                <p>
                  Dice Cat is designed for adults only. You must be at least 18 years old to create an account.
                </p>
              </IonText>
              <IonItem lines="none" className="age-checkbox-item">
                <IonCheckbox
                  slot="start"
                  checked={isOver18}
                  onIonChange={(e) => setIsOver18(e.detail.checked)}
                />
                <IonLabel className="age-checkbox-label">
                  I confirm that I am 18 years of age or older
                </IonLabel>
              </IonItem>
            </div>

            {/* Terms & Privacy Acceptance */}
            <div className="age-verification-box terms-box">
              <div className="age-verification-header">
                <IonIcon icon={documentTextOutline} style={{ fontSize: '24px' }} />
                <h3>Terms & Privacy</h3>
              </div>
              <IonItem lines="none" className="terms-checkbox-item">
                <IonCheckbox
                  slot="start"
                  checked={acceptedTerms}
                  onIonChange={(e) => setAcceptedTerms(e.detail.checked)}
                />
                <IonLabel className="terms-checkbox-label">
                  I agree to the{' '}
                  <a
                    href="https://dice-cat.com/terms.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>
                  {' '}and{' '}
                  <a
                    href="https://dice-cat.com/privacy.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </a>
                </IonLabel>
              </IonItem>
            </div>

            {error && (
              <div className="error-container">
                <IonText color="danger">
                  <p className="error-text">
                    <strong>{error}</strong>
                  </p>
                  {errorDetails && (
                    <details style={{ marginTop: '8px', fontSize: '0.85em' }}>
                      <summary style={{ cursor: 'pointer', opacity: 0.7 }}>
                        Show technical details
                      </summary>
                      <p style={{ marginTop: '4px', fontFamily: 'monospace', fontSize: '0.9em' }}>
                        {errorDetails}
                      </p>
                    </details>
                  )}
                </IonText>
                {error.includes('persists') && (
                  <IonText color="medium" style={{ fontSize: '0.9em', marginTop: '8px', display: 'block' }}>
                    <p>
                      Need help? Email us at{' '}
                      <a href="mailto:support@dice-cat.com" style={{ color: 'var(--ion-color-primary)' }}>
                        support@dice-cat.com
                      </a>
                    </p>
                  </IonText>
                )}
              </div>
            )}

            <IonButton
              expand="block"
              type="submit"
              disabled={loading || !isOver18 || !acceptedTerms}
              className="ion-margin-top"
            >
              {loading ? <IonSpinner name="crescent" /> : 'Sign Up'}
            </IonButton>
          </form>

          <div className="auth-footer">
            <IonButton
              expand="block"
              fill="clear"
              routerLink="/login"
              disabled={loading}
            >
              Already have an account? <strong>Login</strong>
            </IonButton>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Signup;